/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import ImgixWithPrioritizedLoading from 'js/components/ImgixWithPrioritizedLoading';

import { Typography2, breakpoints, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import { getImageAspectRatio } from 'bundles/shared-articles/utils/images';
import type { Asset } from 'bundles/unified-seo-content-common/__generated__/baseContentfulTypes';

export type Props = {
  featuredImage?: Partial<Asset> | null;
  featuredImageCaption?: string | null;
  featuredImageWidth?: number;
  featuredImageHeight?: number;
};

const styles = {
  featuredImageContainer: () => css`
    width: 100%;
    ${breakpoints.down('sm')} {
      width: unset;
      margin-left: -16px;
      margin-right: -16px;
    }
  `,
  featuredImageContainerMargin: css`
    margin-bottom: 3rem;
  `,
  featuredImage: (_: Theme, maxWidth: number, maxHeight: number) => css`
    max-height: ${maxHeight};
    max-width: ${maxWidth};
    width: 100%;
  `,
  featuredImageCaption: () => css`
    color: var(--cds-color-grey-600);
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    margin-top: 1.5rem;
    ${breakpoints.down('sm')} {
      margin-left: 16px;
      margin-right: 16px;
    }
  `,
  horizontalRule: css`
    border-style: solid;
    border-color: var(--cds-color-grey-200);
    margin: 1.5rem 0;
  `,
};

export const ArticleFeaturedImage = ({
  featuredImage,
  featuredImageCaption,
  featuredImageWidth = 1500,
  featuredImageHeight = 680,
}: Props) => {
  const theme = useTheme();

  if (!featuredImage || !featuredImage?.url) {
    return <hr css={styles.horizontalRule} />;
  }

  return (
    <figure
      css={[styles.featuredImageContainer, styles.featuredImageContainerMargin]}
      data-e2e="article-page-featured-image"
    >
      <div
        css={{
          height: 0,
          overflow: 'hidden',
          paddingBottom: `${getImageAspectRatio(featuredImageHeight, featuredImageWidth)}%`,
          width: '100%',
        }}
      >
        <ImgixWithPrioritizedLoading
          {...css(styles.featuredImage(theme, featuredImageWidth, featuredImageHeight))}
          src={featuredImage.url}
          alt={featuredImage.description || ''}
          // Image still appears full-width due to styles applied, this will generate a smaller image from Imgix.
          maxWidth={Math.floor(featuredImageWidth / 1.5)}
          width="100%"
        />
      </div>
      {featuredImageCaption && (
        <Typography2 component="figcaption" variant="bodySecondary" css={styles.featuredImageCaption}>
          {featuredImageCaption}
        </Typography2>
      )}
    </figure>
  );
};

export default ArticleFeaturedImage;
